import { Button, flash, Modal, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { EquipmentAllocationStrategy, Experience, PrioritizedResourceID, UnitType, UserPreferences } from "types";
import { api } from "../../../api/api";
import { useSeller, useUser } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { ConfirmLeaveModal } from "../../Common/ConfirmLeaveModal";
import { RadioInput } from "../../Common/RadioInput";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ContentLayout } from "../../Layout/ContentLayout";
import { EquipmentDragAndDropList } from "./EquipmentDragAndDropList";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { isXolaAdmin } from "@/models/User";

export const EquipmentSettings = () => {
    const [confirm, setConfirm] = useState<boolean>(false);
    const invalidate = useInvalidate();
    const history = useHistory();
    const {
        preferences: { inventory },
        id,
    } = useSeller();

    const user = useUser();
    const [allowEquipmentAsPurchasableUnit, setAllowEquipmentAsPurchasableUnit] = useState(
        inventory.allowEquipmentAsPurchasableUnit,
    );
    const [prioritizedResources, setPrioritizedResources] = useState(inventory.prioritizedResources);
    const [customOrderChanged, setCustomOrderChanged] = useState(false);
    const [allocationStrategy, setAllocationStrategy] = useState<EquipmentAllocationStrategy>(
        inventory.allocationStrategy,
    );

    const { data: equipmentBasedExperiences } = useInfiniteCollection<Experience>("experiences", {
        supportedCatalogItemTypes: UnitType.EQUIPMENT,
        limit: 1,
    });
    const isPurchasableUnitLocked = inventory.allowEquipmentAsPurchasableUnit && equipmentBasedExperiences.length > 0;

    const { mutateAsync: updateGlobalSettings, isLoading: isSaving } = useMutation(
        async (data: UserPreferences["inventory"]) => {
            return api.post(`/users/${id}/preferences/inventory`, data);
        },
    );

    const handleSave = async () => {
        const payload = { ...inventory, allocationStrategy, allowEquipmentAsPurchasableUnit };
        payload.prioritizedResources =
            allocationStrategy === EquipmentAllocationStrategy.CUSTOM_ASSIGNMENT ? prioritizedResources : [];

        await updateGlobalSettings(payload);
        flash.success("Your settings are saved");
        invalidate("sellers", id);
        history.push("/resources/equipment");
    };

    const handleReset = () => {
        setAllocationStrategy(inventory.allocationStrategy);
        setAllowEquipmentAsPurchasableUnit(inventory.allowEquipmentAsPurchasableUnit);
        // wait for state to be updated to suppress confirm leave modal
        setTimeout(() => history.push("/resources/equipment"));
    };

    const isAllocationStrategyChanged = allocationStrategy !== inventory.allocationStrategy;
    const isCustomOrderChanged = !isAllocationStrategyChanged && customOrderChanged;
    const isPurchasableUnitChanged = allowEquipmentAsPurchasableUnit !== inventory.allowEquipmentAsPurchasableUnit;

    return (
        <ContentLayout isWide className="max-w-150 space-y-3">
            <ConfirmLeaveModal
                isFormTouched={
                    !confirm && (isAllocationStrategyChanged || isCustomOrderChanged || isPurchasableUnitChanged)
                }
            />
            <h4 className="font-bold" data-testid="global-settings-title">
                Global Settings
            </h4>
            {isXolaAdmin(user) && (
                <div className="space-y-2">
                    <h5 className="font-bold">Equipment as purchasable unit</h5>
                    <div className="flex items-center">
                        <Tooltip
                            className="flex items-start space-x-3"
                            content="This cannot be turned off while experiences have equipments configured."
                            placement="top"
                            disabled={!isPurchasableUnitLocked}
                        >
                            <Switch
                                isChecked={allowEquipmentAsPurchasableUnit}
                                disabled={isPurchasableUnitLocked}
                                onChange={() => setAllowEquipmentAsPurchasableUnit(!allowEquipmentAsPurchasableUnit)}
                            />
                            <span className="-mt-0.5 text-gray-darker">
                                Allows the seller to configure experiences where the purchasable unit is equipment
                                instead of demographics
                            </span>
                        </Tooltip>
                    </div>
                </div>
            )}

            <h5 className="font-bold">Automatic assignment Rules</h5>

            <EquipmentAllocationOptions
                prioritizedResources={prioritizedResources}
                setPrioritizedResources={setPrioritizedResources}
                setCustomOrderChanged={setCustomOrderChanged}
                allocationStrategy={allocationStrategy}
                onStrategyChange={setAllocationStrategy}
            />

            <AssignmentStrategyWarningInfoModal
                isCustomOrderChanged={isCustomOrderChanged}
                isOpen={confirm}
                handleSave={handleSave}
                setConfirm={setConfirm}
            />

            <ContentLayout.Footer>
                <SubmitButton
                    isLoading={isSaving}
                    onClick={() => {
                        setConfirm(true);
                    }}
                >
                    Save
                </SubmitButton>
                <SecondaryButton disabled={isSaving} onClick={handleReset}>
                    Cancel
                </SecondaryButton>
            </ContentLayout.Footer>
        </ContentLayout>
    );
};

interface EquipmentAllocationOptionsProps {
    experience?: Experience;
    prioritizedResources?: PrioritizedResourceID[];
    allocationStrategy: EquipmentAllocationStrategy;
    onStrategyChange: (strategy: EquipmentAllocationStrategy) => void;
    setPrioritizedResources: (value: PrioritizedResourceID[]) => void;
    setCustomOrderChanged: (value: boolean) => void;
}

export const EquipmentAllocationOptions = ({
    experience,
    prioritizedResources,
    setPrioritizedResources,
    setCustomOrderChanged,
    allocationStrategy,
    onStrategyChange,
}: EquipmentAllocationOptionsProps) => {
    return (
        <>
            <RadioInput
                className="cursor-pointer"
                classNames={{ input: "self-start" }}
                checked={allocationStrategy === EquipmentAllocationStrategy.LARGEST_FIRST}
                name="buttonType"
                value={EquipmentAllocationStrategy.LARGEST_FIRST}
                onChange={() => onStrategyChange(EquipmentAllocationStrategy.LARGEST_FIRST)}
            >
                <div className="ml-3 -translate-y-1 text-gray-darker">
                    <h5 className="mb-1 font-bold text-black">Assign largest equipment first</h5>
                    Equipment with the highest capacity will be assigned before equipment with lower capacity. Choose
                    this option to encourage more purchases within the same event.
                </div>
            </RadioInput>

            <RadioInput
                className="cursor-pointer"
                classNames={{ input: "self-start" }}
                checked={allocationStrategy === EquipmentAllocationStrategy.SMALLEST_FIRST}
                name="buttonType"
                value={EquipmentAllocationStrategy.SMALLEST_FIRST}
                onChange={() => onStrategyChange(EquipmentAllocationStrategy.SMALLEST_FIRST)}
            >
                <div className="ml-3 -translate-y-1 text-gray-darker">
                    <h5 className="mb-1 font-bold text-black">Assign smallest equipment first</h5>
                    Equipment with the lowest capacity will be assigned before equipment with higher capacity. Choose
                    this option to encourage purchases across different events.
                </div>
            </RadioInput>

            <RadioInput
                className="cursor-pointer"
                classNames={{ input: "self-start" }}
                checked={allocationStrategy === EquipmentAllocationStrategy.CUSTOM_ASSIGNMENT}
                name="buttonType"
                value={EquipmentAllocationStrategy.CUSTOM_ASSIGNMENT}
                onChange={() => onStrategyChange(EquipmentAllocationStrategy.CUSTOM_ASSIGNMENT)}
            >
                <div className="ml-3 -translate-y-1 text-gray-darker">
                    <h5 className="mb-1 font-bold text-black">Assign equipment in custom order</h5>
                    Equipment will be assigned in order configured below as applicable to the product.
                </div>
            </RadioInput>

            {allocationStrategy === EquipmentAllocationStrategy.CUSTOM_ASSIGNMENT && (
                <EquipmentDragAndDropList
                    experience={experience}
                    prioritizedResources={prioritizedResources}
                    setPrioritizedResources={setPrioritizedResources}
                    setCustomOrderChanged={setCustomOrderChanged}
                />
            )}
        </>
    );
};

interface AssignmentStrategyWarningInfoModalProps {
    isOpen: boolean;
    isCustomOrderChanged?: boolean;
    handleSave: () => void;
    setConfirm: (confirm: boolean) => void;
}

export const AssignmentStrategyWarningInfoModal = ({
    isOpen,
    isCustomOrderChanged,
    handleSave,
    setConfirm,
}: AssignmentStrategyWarningInfoModalProps) => {
    return (
        <Modal
            shouldCloseOnOutsideClick
            isOpen={isOpen}
            size="medium"
            onClose={() => {
                setConfirm(false);
            }}
        >
            <Modal.Header>{isCustomOrderChanged ? "Confirm Change" : "Warning"}</Modal.Header>
            <p className="mt-6">
                {isCustomOrderChanged
                    ? "The equipment assignment order has been changed. These updates will apply to all new purchases. Please note that existing equipment assignments will remain unchanged."
                    : "Switching strategies will cause re-assignment of equipment for all upcoming events. This may result in overbooking for some events."}
            </p>
            <Modal.Footer className="space-x-4">
                <SecondaryButton
                    onClick={() => {
                        setConfirm(false);
                    }}
                >
                    Cancel
                </SecondaryButton>
                <Button color={isCustomOrderChanged ? "primary" : "danger"} onClick={handleSave}>
                    Confirm
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
